import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import ErrorMessage from '@/components/shared/ErrorMessage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Table } from '@/components/ui/daisy/table';
import { getErrorMessage } from '@/utils/getErrorMessage';

import { Loading } from '../../loading';
import { resolveVisibleColumns } from './resolveVisibleColumns';
import { SmartTableProps } from './types';

export * from './types';

export function SmartTable<T, K extends string = string>({
  columns,
  order,
  data,
  getRowKey,
  isLoading = false,
  isFetching = false,
  error,
  emptyMessage,
  onRetry,
  classNames,
  ...tableProps
}: SmartTableProps<T, K>) {
  const visibleColumns = useMemo(
    () => resolveVisibleColumns(columns, order),
    [columns, order],
  );

  const isEmpty = !isLoading && !error && data.length === 0;
  const isRefreshing = isFetching && !isLoading;

  return (
    <Table.Container className={twMerge('relative', classNames?.container)}>
      <ConditionalRenderer condition={isRefreshing}>
        <Loading
          variant="spinner"
          size="xs"
          color="primary"
          className="absolute right-3 top-3"
        />
      </ConditionalRenderer>
      <Table aria-busy={isRefreshing} {...tableProps}>
        <Table.Head>
          <Table.Row>
            {visibleColumns.map(({ key, header, headerClassName }) => (
              <Table.HeaderCell
                key={key}
                className={twMerge(classNames?.header, headerClassName)}
              >
                {header}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body
          className={twMerge(isRefreshing && 'opacity-50 transition-opacity')}
        >
          <ConditionalRenderer condition={isLoading}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <div className="flex w-full justify-center py-6">
                  <Loading color="primary" size="sm" />
                </div>
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={!!error}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <ErrorMessage
                  className="py-6"
                  text={getErrorMessage(error)}
                  onRetry={onRetry}
                />
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={isEmpty}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <Text
                  format="rubik-400"
                  className="flex w-full justify-center py-6 text-neutral"
                  text={emptyMessage}
                />
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={!isLoading}>
            {data.map(item => (
              <Table.Row key={getRowKey(item)}>
                {visibleColumns.map(({ key, Cell, cellClassName }) => (
                  <Table.Cell key={key} className={cellClassName}>
                    <Cell item={item} />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </ConditionalRenderer>
        </Table.Body>
      </Table>
    </Table.Container>
  );
}
